import React from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import loadable from '@loadable/component';
import { NoSsr } from '@99designs/emu';
import { FullscreenError } from '@99designs/layout';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Site } from '@99designs/site-navigation/lib/Site';
import { Loading } from '@99designs/spa-common';
import { useNavigationTracking } from '@99designs/tracking';
import { getViewerRole, useViewer } from '@99designs/viewer';
import Layout from './common/Layout/Layout';
import AuthenticatedRoute from './common/Routing/AuthenticatedRoute';
import DotComOnlyRoute from './common/Routing/DotComOnlyRoute';
import FullscreenSVAError from './pages/auth/SSO/ErrorPage/ErrorPage';

const Messages = loadable(() => import('./pages/messages'));

const ProjectsApp = loadable(() => import('./pages/projects/ProjectsApp'));
const AuthPageContainer = loadable(() => import('./pages/auth/PageContainer'));
const VerifyAddPayoutMethod = loadable(() => import('./pages/auth/VerifyAddPayoutMethod'));
const VerifyAddPayoutRequest = loadable(() => import('./pages/auth/VerifyAddPayoutRequest'));
const Notifications = loadable(() => import('./pages/notifications'));
const Status = loadable(() => import('./pages/status'));
const Account = loadable(() => import('./pages/account'));
const HandoverMessages = loadable(() => import('./pages/handover/messages'));
const Create = loadable(() => import('./pages/create'));
const YourProjectsApp = loadable(() => import('./pages/your-projects/YourProjectsApp'));
const YourVistaProjectsApp = loadable(
    () => import('./pages/your-vista-projects/YourVistaProjectsApp')
);
const YourWorkApp = loadable(() => import('./pages/your-work/YourWorkApp'));
const AdminRedirects = loadable(() => import('./pages/admin-redirects'));

const BrowseProjects = loadable(() => import('./pages/browse-projects/BrowseProjects'));
const JobDetailsPage = loadable(() => import('./pages/browse-projects/JobDetailsPage'));
const JobApplyPage = loadable(() => import('./pages/browse-projects/JobApplyPage'));
const CreatorApplication = loadable(() => import('./pages/onboarder/CreatorApplicationPage'));

const NewUserPage = loadable(() => import('./pages/auth/SSO/NewUserPage'));
const NewUserByRoleSelectionPage = loadable(
    () => import('./pages/auth/SSO/NewUserByRoleSelectionPage')
);
const NewUserByMarkingOptInPage = loadable(
    () => import('./pages/auth/SSO/NewUserByMarketingOptInPage')
);
const UpdatedTermsAndConditionsPage = loadable(
    () => import('./pages/auth/UpdatedTermsAndConditionsPage')
);

const MdsaAgreementPage = loadable(
    () => import('./pages/master-design-services-agreement/MdsaAgreementPage')
);
const MdsaConfirmationPage = loadable(
    () => import('./pages/master-design-services-agreement/MdsaConfirmationPage')
);

const ProductCatalog = loadable(() => import('@99designs/feature-product-catalog'), {
    resolveComponent: (components) => components.ProductCatalog,
});
const ProductWizard = loadable(() => import('@99designs/feature-product-catalog'), {
    resolveComponent: (components) => components.ProductWizard,
});
const ProductGroups = loadable(() => import('@99designs/feature-product-catalog'), {
    resolveComponent: (components) => components.ProductGroups,
});
const ProductView = loadable(() => import('@99designs/feature-product-catalog'), {
    resolveComponent: (components) => components.ProductView,
});

export default function SpaRoutes({ site }: { site: Site }) {
    useNavigationTracking();
    const viewer = useViewer();
    const role = getViewerRole(viewer);

    function AdminOnlyRoute() {
        if (role !== 'admin') {
            return <FullscreenError errorCode="404" />;
        }
        return <Outlet />;
    }

    return (
        <Routes>
            <Route path="/admin-redirect/*" element={<AdminRedirects />} />
            <Route
                path="/login"
                element={<AuthPageContainer fallback={<Loading />} initialPage="login" />}
            />
            <Route
                path="/signup"
                element={<AuthPageContainer fallback={<Loading />} initialPage="signup" />}
            />
            <Route
                path="/signup/:variant"
                element={<AuthPageContainer fallback={<Loading />} initialPage="signup" />}
            />
            <Route
                path="/auth/verify"
                element={<AuthPageContainer fallback={<Loading />} initialPage="verifyLogin" />}
            />
            <Route path="/auth/sso/new-user" element={<NewUserPage fallback={<Loading />} />} />
            <Route
                path="/auth/sso/role-selection"
                element={<NewUserByRoleSelectionPage fallback={<Loading />} />}
            />
            <Route
                path="/auth/sso/opt-in"
                element={<NewUserByMarkingOptInPage fallback={<Loading />} />}
            />
            <Route
                path="/briefs/*"
                element={
                    <NoSsr fallback={<Loading />}>
                        <Create />
                    </NoSsr>
                }
            />
            <Route
                path="/squarespace/*"
                element={
                    <NoSsr fallback={<Loading />}>
                        <Create />
                    </NoSsr>
                }
            />
            {/* Authenticated Routes - No layout */}
            <Route element={<AuthenticatedRoute children={<Outlet />} />}>
                <Route path="/products/admin" element={<AdminOnlyRoute />}>
                    <Route index element={<ProductCatalog fallback={<Loading />} />} />
                    <Route path="list" element={<ProductCatalog fallback={<Loading />} />}>
                        {['drafts', 'awaiting-translations', 'live', 'archived'].map((path) => (
                            <Route key={path}>
                                <Route path={path} />
                                <Route path={`${path}/page/:page`} />
                            </Route>
                        ))}
                    </Route>
                    <Route path="groups" element={<ProductGroups fallback={<Loading />} />} />
                    <Route path="create" element={<ProductWizard fallback={<Loading />} />} />
                    <Route
                        path=":productKey"
                        element={
                            <NoSsr fallback={<Loading />}>
                                <ProductView />
                            </NoSsr>
                        }
                    />
                    <Route
                        path=":productKey/edit"
                        element={
                            <NoSsr fallback={<Loading />}>
                                <ProductWizard fallback={<Loading />} />
                            </NoSsr>
                        }
                    />
                </Route>

                <Route path="/work" element={<Navigate to="/work/all" replace />} />
                <Route
                    path="/work/:status"
                    element={
                        <Layout site={site} footerStyle="reduced">
                            <YourWorkApp />
                        </Layout>
                    }
                />

                <Route
                    path="/account/application/*"
                    element={
                        <NoSsr fallback={<Loading />}>
                            <CreatorApplication site={site} />
                        </NoSsr>
                    }
                />
                <Route
                    path="/account/updated-terms-and-conditions"
                    element={<UpdatedTermsAndConditionsPage fallback={<Loading />} />}
                />
                <Route path="/projects/job/:jobId/apply/*" element={<JobApplyPage />} />
                <Route
                    path="/legal/mdsa"
                    element={
                        <DotComOnlyRoute site={site}>
                            <MdsaAgreementPage fallback={<Loading />} />
                        </DotComOnlyRoute>
                    }
                />
                <Route
                    path="/legal/mdsa/confirmation"
                    element={
                        <DotComOnlyRoute site={site}>
                            <MdsaConfirmationPage fallback={<Loading />} />
                        </DotComOnlyRoute>
                    }
                />
            </Route>

            {/* Routes that also need to render the standard Layout such as GlobalNav, Footer etc */}
            <Route element={<Layout site={site} children={<Outlet />} />}>
                {/* Unauthenticated routes */}
                <Route path="/auth/sso/error" element={<FullscreenSVAError />} />
                <Route
                    path="/auth/verify-payout"
                    element={<VerifyAddPayoutMethod fallback={<Loading />} />}
                />
                <Route
                    path="/auth/verify-new-payout"
                    element={<VerifyAddPayoutRequest fallback={<Loading />} />}
                />

                <Route path="/spa/status" element={<Status fallback={<Loading />} />} />

                {/* Authenticated Routes */}
                <Route element={<AuthenticatedRoute children={<Outlet />} />}>
                    <Route path="/account/*" element={<Account fallback={<Loading />} />} />
                    <Route path="/messages" element={<Messages fallback={<Loading />} />} />
                    <Route
                        path="/messages/:recipientId"
                        element={<Messages fallback={<Loading />} />}
                    />
                    <Route
                        path="/notifications"
                        element={<Notifications fallback={<Loading />} />}
                    />
                    <Route
                        path="/projects/browse/*"
                        element={
                            <NoSsr fallback={<Loading />}>
                                <BrowseProjects />
                            </NoSsr>
                        }
                    />
                    <Route
                        path="/projects/job/:jobId/*"
                        element={
                            <NoSsr fallback={<Loading />}>
                                <JobDetailsPage />
                            </NoSsr>
                        }
                    />
                    {[
                        '/projects/dashboard',
                        '/projects/dashboard/:status',
                        '/spa/projects/dashboard',
                        '/spa/projects/dashboard/:status',
                    ].map((path) => (
                        <Route key={path} path={path} element={<YourProjectsApp />} />
                    ))}
                    {[
                        '/vista-projects/dashboard',
                        '/vista-projects/dashboard/:state',
                        '/spa/vista-projects/dashboard',
                        '/spa/vista-projects/dashboard/:state',
                    ].map((path) => (
                        <Route key={path} path={path} element={<YourVistaProjectsApp />} />
                    ))}
                    <Route path="/projects/:projectId/*" element={<ProjectsApp />} />
                    <Route path="/spa/projects/:projectId/*" element={<ProjectsApp />} />
                    <Route path="/handover/:handoverId/messages" element={<HandoverMessages />} />
                </Route>

                {/* Catch-all Error page */}
                <Route path="*" element={<FullscreenError errorCode="404" />} />
            </Route>
        </Routes>
    );
}
